import { Head, router } from '@inertiajs/react'
import { useState } from 'react'
import AdminLayout from '../../../components/layout/AdminLayout'
import StatsCard from '../../../components/dashboard/StatsCard'
import LineChart from '../../../components/charts/LineChart'

export default function AdminAnalyticsIndex({
  globalStats,
  topAgencies,
  leadsOverTime,
  scansOverTime,
  period
}) {
  const [selectedPeriod, setSelectedPeriod] = useState(period)

  const handlePeriodChange = (newPeriod) => {
    setSelectedPeriod(newPeriod)
    router.get('/admin/analytics', { period: newPeriod }, { preserveState: true })
  }

  return (
    <AdminLayout>
      <Head title="Analytics Admin" />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Analytics globales</h1>
            <p className="text-gray-600 mt-1">
              Vue d'ensemble des performances de la plateforme
            </p>
          </div>

          <div className="flex gap-2">
            {['week', 'month', 'year'].map(p => (
              <button
                key={p}
                onClick={() => handlePeriodChange(p)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedPeriod === p
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {p === 'week' && 'Semaine'}
                {p === 'month' && 'Mois'}
                {p === 'year' && 'Année'}
              </button>
            ))}
          </div>
        </div>

        {/* KPIs Globaux */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatsCard
            title="Agences"
            value={globalStats.agencies.total}
            subtitle={`${globalStats.agencies.active} actives`}
            icon="building"
            color="blue"
          />

          <StatsCard
            title="Leads"
            value={globalStats.leads.total}
            subtitle={`${globalStats.leads.this_period} cette période`}
            icon="users"
            color="green"
          />

          <StatsCard
            title="QR Codes"
            value={globalStats.qr_codes.total}
            subtitle={`${globalStats.qr_codes.active} actifs`}
            icon="qrcode"
            color="purple"
          />

          <StatsCard
            title="Revenue"
            value={`${(globalStats.revenue.total / 100).toFixed(0)}€`}
            subtitle={`${(globalStats.revenue.this_period / 100).toFixed(0)}€ cette période`}
            icon="currency"
            color="yellow"
          />
        </div>

        {/* Graphiques d'évolution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Évolution des leads</h2>
            <LineChart
              data={leadsOverTime}
              color="#3B82F6"
              label="Leads"
            />
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Évolution des scans</h2>
            <LineChart
              data={scansOverTime}
              color="#10B981"
              label="Scans"
            />
          </div>
        </div>

        {/* Top Agences */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Top 10 Agences</h2>
            <p className="text-sm text-gray-600 mt-1">Classement par nombre de leads générés</p>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {topAgencies && topAgencies.length > 0 ? (
                topAgencies.map((agency, index) => (
                  <div key={agency.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-600 font-semibold">
                        {index + 1}
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">{agency.name}</h3>
                        <p className="text-sm text-gray-500">ID: {agency.id}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-gray-900">{agency.leadsCount}</div>
                      <div className="text-xs text-gray-500">leads</div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-8">Aucune donnée disponible</p>
              )}
            </div>
          </div>
        </div>

        {/* Statistiques détaillées */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600 mb-2">Agences avec leads</div>
            <div className="text-3xl font-bold text-gray-900">{globalStats.agencies.with_leads}</div>
            <p className="text-xs text-gray-500 mt-2">
              {((globalStats.agencies.with_leads / globalStats.agencies.total) * 100).toFixed(1)}% du total
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600 mb-2">Scans cette période</div>
            <div className="text-3xl font-bold text-gray-900">{globalStats.qr_codes.scans_this_period}</div>
            <p className="text-xs text-gray-500 mt-2">Via QR codes</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600 mb-2">Revenue moyen par agence</div>
            <div className="text-3xl font-bold text-gray-900">
              {globalStats.agencies.total > 0
                ? ((globalStats.revenue.total / 100) / globalStats.agencies.total).toFixed(0)
                : 0}€
            </div>
            <p className="text-xs text-gray-500 mt-2">Depuis le début</p>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}
